import { useEffect, useMemo, useState } from "react";
import { ClerkProvider } from "@clerk/react";
import { useTheme } from "next-themes";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { getClerkAppearance } from "../lib/clerkAppearance";

const ENV_KEY = (
  process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ||
  process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY ||
  process.env.CLERK_PUBLISHABLE_KEY ||
  ""
).trim();

export default function ClerkRoot({ children }) {
  const { resolvedTheme } = useTheme();
  const [publishableKey, setPublishableKey] = useState(ENV_KEY || null);
  const [failed, setFailed] = useState(false);

  const appearance = useMemo(
    () => getClerkAppearance(resolvedTheme === "light" ? "light" : "dark"),
    [resolvedTheme],
  );

  useEffect(() => {
    if (publishableKey) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/clerk/config");
        if (!cancelled && data?.enabled && data.publishable_key) {
          setPublishableKey(data.publishable_key);
          return;
        }
        if (!cancelled) setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publishableKey]);

  if (failed) {
    return (
      <div className="min-h-screen bg-tribal grid place-items-center px-6 text-center text-sm text-[color:var(--text-secondary)]">
        <p>
          Authentification Clerk non configurée. Définissez{" "}
          <code className="text-[color:var(--primary)]">CLERK_PUBLISHABLE_KEY</code> sur le serveur
          ou <code className="text-[color:var(--primary)]">REACT_APP_CLERK_PUBLISHABLE_KEY</code> au build.
        </p>
      </div>
    );
  }

  if (!publishableKey) {
    return (
      <div className="min-h-screen bg-tribal grid place-items-center">
        <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl="/"
      appearance={appearance}
    >
      {children}
    </ClerkProvider>
  );
}
